import React, { FC, useCallback, useMemo, useState } from 'react';
import { IHandledDocument, IHandledDocumentPart, ISearchProfile } from '../../../types';
import { DocumentNamePosition } from '../types';
import { useHighlights } from '../../../hooks';
import styles from './index.module.scss';
import boxStyles from 'src/shared/Tender/Boxes/EditableBox/index.module.scss';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { notification } from 'src/common';
import { EditableBoxAction } from 'src/shared';
import GeneratedPart from './GeneratedPart';
import { updateCacheAddTenderBox, useAddTenderBox, useTenderData } from 'src/models/procurements/Tender/hooks';
import classNames from 'classnames';
import { TenderBoxFieldType } from '@tendium/prom-types/tender';
import { TenderFile } from 'src/shared';
import { toPdfHighlightsData } from 'src/shared/DocumentViewer/helpers';

export interface IPartProps {
  part: IHandledDocumentPart;
  document: IHandledDocument;
  namePosition: DocumentNamePosition;
  mainSearchProfile: ISearchProfile | null;
}

const Part: FC<IPartProps> = props => {
  const { part, document: doc, namePosition, mainSearchProfile } = props;
  const { content, headline } = part;
  const { id: procurementId } = useParams<{ id: string }>();
  const { data: model } = useTenderData();
  const editable = !!model?.isEditable;
  const bidId = useMemo(() => {
    return model?.bid?.id;
  }, [model]);

  const { t } = useTranslation();

  const chunks = useHighlights(part, mainSearchProfile);

  const [tenderBoxAdded, setTenderBoxAdded] = useState(false);
  const [addTenderBox, { loading: tenderBoxAdding }] = useAddTenderBox();

  const highlights = useMemo(() => toPdfHighlightsData(part.headlinePosition, part.contentPosition), [part]);

  const onAdd = useCallback(() => {
    procurementId &&
      bidId &&
      addTenderBox({
        variables: {
          id: procurementId,
          bidId,
          box: {
            title: headline,
            category: 'OTHER.OTHER',
            fields: [
              {
                name: 'desc',
                type: TenderBoxFieldType.String,
                string: content
              }
            ]
          }
        },
        update: updateCacheAddTenderBox(procurementId)
      })
        .then(res => {
          setTenderBoxAdded(true);
          return res;
        })
        .catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
  }, [addTenderBox, bidId, content, headline, procurementId, t]);
  return (
    <div
      className={classNames(styles.container, {
        [boxStyles.editableBox]: editable,
        [styles.editable]: editable
      })}
    >
      <div className={classNames(styles.header, { [boxStyles.header]: editable })}>
        <h3 className={styles.title}>
          <GeneratedPart chunks={chunks.headline} content={headline} mainSearchProfile={mainSearchProfile} />
        </h3>
        {editable && (
          <EditableBoxAction type={'add'} onAction={onAdd} isLoading={tenderBoxAdding} isDone={tenderBoxAdded} />
        )}
      </div>
      <div className={styles.content}>
        <GeneratedPart chunks={chunks.content} content={content} mainSearchProfile={mainSearchProfile} />
      </div>
      {namePosition === DocumentNamePosition.PartNode && procurementId && (
        <div className={styles.meta}>
          <div className={styles.metaLabel}>{t('KeywordSearch.foundInDocument')}</div>
          <TenderFile
            procurementId={procurementId}
            targetPath={doc.filePath}
            displayName={doc.fileName}
            pdfHighlights={highlights}
            eventSource="Highlight Keywords"
          />
        </div>
      )}
    </div>
  );
};

export default Part;
